import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import useAuthStore from '../store/useAuthStore';

const PLAN_OPTIONS = [
  { value: 'free_trial', label: 'Free Trial (14 days)' },
  { value: 'basic', label: 'Basic Plan' },
  { value: 'pro', label: 'Pro Plan' },
];

export default function Register() {
  const navigate = useNavigate();
  const login = useAuthStore((state) => state.login);
  const directRegisterAllowed =
    process.env.NODE_ENV !== 'production' || process.env.REACT_APP_ALLOW_DIRECT_REGISTER === 'true';

  const [form, setForm] = useState({
    clinicName: '',
    doctorName: '',
    email: '',
    mobileNumber: '',
    password: '',
    confirmPassword: '',
    planType: 'free_trial',
    trialDays: 14,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const updateField = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const redirectAfterLogin = (user) => {
    navigate(user?.setupCompleted ? '/dashboard' : '/setup', { replace: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (form.password !== form.confirmPassword) {
      setError('Password and confirm password must match.');
      return;
    }

    if (form.password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    const normalizedMobile = form.mobileNumber.trim();
    if (!normalizedMobile) {
      setError('Mobile number is required.');
      return;
    }

    if (!/^[0-9+\-\s()]{8,20}$/.test(normalizedMobile)) {
      setError('Enter a valid mobile number.');
      return;
    }

    setLoading(true);

    try {
      await api.post('/tenants/provision', {
        clinicName: form.clinicName.trim(),
        doctorName: form.doctorName.trim(),
        email: form.email.trim().toLowerCase(),
        mobileNumber: normalizedMobile,
        password: form.password,
        planType: form.planType,
        trialDays: Number(form.trialDays) || 14,
      });

      const { data } = await api.post('/auth/login', {
        email: form.email.trim().toLowerCase(),
        password: form.password,
      });

      login(data.token, data.user);
      redirectAfterLogin(data.user);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to create clinic account');
    } finally {
      setLoading(false);
    }
  };

  if (!directRegisterAllowed) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-xl rounded-3xl bg-white p-8 shadow-xl text-center">
          <div className="text-xs font-semibold uppercase tracking-[0.25em] text-indigo-500">Paid onboarding required</div>
          <h1 className="mt-3 text-3xl font-bold text-slate-900">Create Clinic Workspace</h1>
          <p className="mt-3 text-sm text-slate-600">
            In production, a clinic workspace is created only after plan selection, Google sign-in, Razorpay payment, and backend payment verification.
          </p>
          <Link to="/subscribe" className="mt-6 inline-flex rounded-2xl bg-indigo-600 px-5 py-3 font-semibold text-white">
            Subscribe first
          </Link>
          <div className="mt-5 text-sm text-slate-500">
            Existing clinic? <Link to="/login" className="font-semibold text-indigo-700">Sign in</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-5xl grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="rounded-3xl bg-white shadow-xl p-8">
          <div className="mb-8 text-center">
            <div className="text-xs font-semibold uppercase tracking-[0.25em] text-indigo-500">Clinic OS</div>
            <h1 className="mt-3 text-3xl font-bold text-slate-900">Create Clinic Workspace</h1>
            <p className="mt-2 text-sm text-slate-500">
              Register your clinic and get a private Dektal 360 Pro tenant with isolated data.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Clinic Name</label>
              <input
                type="text"
                name="clinicName"
                value={form.clinicName}
                onChange={updateField}
                placeholder="Smile Craft Dental"
                className="w-full rounded-2xl border border-slate-300 px-4 py-3 outline-none focus:border-indigo-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Lead Doctor Name</label>
              <input
                type="text"
                name="doctorName"
                value={form.doctorName}
                onChange={updateField}
                placeholder="Dr. Priya Shah"
                className="w-full rounded-2xl border border-slate-300 px-4 py-3 outline-none focus:border-indigo-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={updateField}
                placeholder="doctor@clinic.com"
                className="w-full rounded-2xl border border-slate-300 px-4 py-3 outline-none focus:border-indigo-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Mobile Number</label>
              <input
                type="tel"
                name="mobileNumber"
                value={form.mobileNumber}
                onChange={updateField}
                placeholder="+91 98765 43210"
                className="w-full rounded-2xl border border-slate-300 px-4 py-3 outline-none focus:border-indigo-500"
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
                <input
                  type="password"
                  name="password"
                  value={form.password}
                  onChange={updateField}
                  placeholder="Minimum 8 characters"
                  className="w-full rounded-2xl border border-slate-300 px-4 py-3 outline-none focus:border-indigo-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Confirm Password</label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={form.confirmPassword}
                  onChange={updateField}
                  placeholder="Re-enter password"
                  className="w-full rounded-2xl border border-slate-300 px-4 py-3 outline-none focus:border-indigo-500"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Plan</label>
                <select
                  name="planType"
                  value={form.planType}
                  onChange={updateField}
                  className="w-full rounded-2xl border border-slate-300 px-4 py-3 outline-none focus:border-indigo-500"
                >
                  {PLAN_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Trial Days</label>
                <input
                  type="number"
                  name="trialDays"
                  min={1}
                  max={90}
                  value={form.trialDays}
                  onChange={updateField}
                  className="w-full rounded-2xl border border-slate-300 px-4 py-3 outline-none focus:border-indigo-500"
                  disabled={form.planType !== 'free_trial'}
                />
              </div>
            </div>

            {error ? (
              <div className="rounded-2xl bg-rose-50 px-4 py-3 text-sm text-rose-600">{error}</div>
            ) : null}

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-2xl bg-indigo-600 px-4 py-3 text-white font-semibold disabled:opacity-60"
            >
              {loading ? 'Creating workspace...' : 'Create Clinic Workspace'}
            </button>
          </form>

          <div className="mt-6 text-center text-sm text-slate-600">
            Already have an account?{' '}
            <Link to="/login" className="font-semibold text-indigo-600 hover:text-indigo-700">
              Sign in
            </Link>
          </div>
        </div>

        <div className="rounded-3xl bg-white shadow-xl p-8">
          <h2 className="text-2xl font-bold text-slate-900">What you get</h2>
          <div className="mt-5 space-y-4 text-sm text-slate-600">
            <div className="rounded-2xl bg-slate-50 p-4">
              Separate clinic tenant with strict `tenantId` isolation.
            </div>
            <div className="rounded-2xl bg-slate-50 p-4">
              Private setup, patients, invoices, inventory, and reports for your clinic only.
            </div>
            <div className="rounded-2xl bg-slate-50 p-4">
              Automatic routing to setup after signup so onboarding starts immediately.
            </div>
          </div>

          <div className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
            No data from any other clinic is visible in your workspace.
          </div>
        </div>
      </div>
    </div>
  );
}
